import { useState, useMemo } from 'react';

import Button from '~/shared/components/Buttons';
import SearchBar from '~/shared/components/SearchBar';
import Typography from '~/shared/components/Typography';
import Loader from '~/shared/components/Loader';
import { usePermission } from '~/shared/hooks/usePermission';

import { getTreeItem } from '../helpers/treeDataLoader';
import { countNodesInTree } from '../helpers/mergeTreeData';
import { filterTreeItemsBySearch } from '../helpers/filterTreeItems';
import { calculateVerticalLines } from '../helpers/calculateVerticalLines';

import HeadlessTreeNode from './HeadlessTreeNode';
import { useConfigurationTree } from './useConfigurationTree';
import AddNewConfigurationModal from './AddNewConfigurationModal';

interface ConfigurationTreeProps {
  selectedNodeId: string | null;
  onSelectNode: (nodeId: string) => void;
}

export default function ConfigurationTree({
  selectedNodeId,
  onSelectNode,
}: ConfigurationTreeProps) {
  const canWriteConfig = usePermission('datafy.write.config');
  const [searchQuery, setSearchQuery] = useState('');
  const { tree, treeData, isLoading, hasData } = useConfigurationTree('edited', searchQuery);
  const [showAddModal, setShowAddModal] = useState(false);

  const allItems = tree.getItems();

  const items = useMemo(
    () => filterTreeItemsBySearch(allItems, searchQuery),
    [allItems, searchQuery]
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleAddClick = () => {
    setShowAddModal(true);
  };

  const handleModalClose = () => {
    setShowAddModal(false);
  };

  const handleModalNext = (selectedNodeId: string) => {
    // TODO: Implement next step logic (e.g., open configuration form for selected item)
    setShowAddModal(false);
  };

  return (
    <div className="flex flex-col h-full py-4 px-5">
      <div className="py-2">
        <SearchBar
          size="sm"
          placeholder="Search"
          value={searchQuery}
          onChange={handleSearchChange}
          className="w-full"
        />
      </div>

      {canWriteConfig && (
        <div className="py-2">
          <Button variant="primary" className="w-full" onClick={handleAddClick}>
            <span className="text-[22px] leading-none -mt-1">+</span> Add new configuration
          </Button>
        </div>
      )}

      <div
        className="flex-1 min-h-0 overflow-y-auto side-panel-scrollbar"
        style={{ direction: 'rtl' }}
        {...tree.getContainerProps('Configurations')}
      >
        <div style={{ direction: 'ltr' }}>
          {items.length === 0 ? (
            isLoading && !hasData ? (
              <div className="flex flex-1 justify-center items-center h-full">
                <Loader variant="xl" />
              </div>
            ) : (
              <div className="p-4 text-center text-primary-900">
                <Typography variant="p2">No items found</Typography>
              </div>
            )
          ) : (
            <div className="flex flex-col gap-1 relative">
              {calculateVerticalLines(items).map(({ top, height, left, key }) => (
                <div
                  key={key}
                  className="absolute w-[1px] bg-primary-100 z-20"
                  style={{
                    left: `${left}px`,
                    top: `${top}px`,
                    height: `${height}px`,
                  }}
                />
              ))}
              {items.map((item) => {
                const node =
                  treeData && item.isFolder() ? getTreeItem(item.getId(), treeData) : undefined;
                const stableCount = node !== undefined ? countNodesInTree(node) - 1 : undefined;
                return (
                  <HeadlessTreeNode
                    key={item.getId()}
                    item={item}
                    selectedNodeId={selectedNodeId}
                    onSelect={onSelectNode}
                    hasChevron={item.isFolder()}
                    count={item.isFolder() ? stableCount : undefined}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>

      <AddNewConfigurationModal
        isOpen={showAddModal}
        onClose={handleModalClose}
        onNext={handleModalNext}
      />
    </div>
  );
}
